import { formatDate, getDateWithoutTime } from "@endya-conformite/ui-kit";

export type AxeFileUploader = "ADHERENT" | "VERIFICATEUR";

/**
 * État du contrôle au dépôt de la pièce, estampillé à l'écriture côté back.
 * Null sur les pièces antérieures à cette information.
 */
export type AxeFileContexteDepot = "INITIAL" | "REMEDIATION" | "DEPOT";

export type GroupableAxeFile = {
  createdAt: Date | string;
  uploadBy?: AxeFileUploader | null;
  contexteDepot?: AxeFileContexteDepot | null;
  dateMiseEnRemediation?: Date | string | null;
};

export type AxeFileGroupKind = "DEPOT_INITIAL" | "DEPOT" | "REMEDIATION";

export type AxeFileGroup<T> = {
  key: string;
  title: string;
  kind: AxeFileGroupKind;
  files: T[];
};

const DEPOT_INITIAL_KEY = "depot-initial";

type GroupAccumulator<T> = {
  key: string;
  kind: AxeFileGroupKind;
  files: T[];
  sortTime: number | null;
  earliestTime: number;
  titleTime: number | null;
};

/**
 * Une pièce déposée pendant une remédiation du contrôle la rejoint ; sinon elle
 * va au dépôt initial, ou à un dépôt daté si le contrôle avait déjà connu une
 * remédiation. Les pièces du vérificateur hors remédiation restent datées : le
 * dépôt initial est la collecte de l'adhérent.
 */
function getKind(file: GroupableAxeFile): AxeFileGroupKind {
  if (file.contexteDepot === "REMEDIATION") return "REMEDIATION";
  if (file.contexteDepot === "DEPOT") return "DEPOT";
  return (file.uploadBy ?? null) === "VERIFICATEUR" ? "DEPOT" : "DEPOT_INITIAL";
}

export function buildAxeFileGroups<T extends GroupableAxeFile>(
  files: T[]
): AxeFileGroup<T>[] {
  const groups = new Map<string, GroupAccumulator<T>>();

  files.forEach((file) => {
    const fileTime = new Date(file.createdAt).getTime();
    const kind = getKind(file);
    const remediationTime = file.dateMiseEnRemediation
      ? new Date(file.dateMiseEnRemediation).getTime()
      : null;

    const key = (() => {
      if (kind === "DEPOT_INITIAL") return DEPOT_INITIAL_KEY;
      if (kind === "REMEDIATION") return `remediation-${remediationTime ?? 0}`;
      const day = getDateWithoutTime(new Date(file.createdAt)).getTime();
      // La remédiation en vigueur au dépôt sépare deux blocs du même jour que
      // la mise en remédiation encadre.
      return `depot-${day}-${remediationTime ?? "initial"}`;
    })();

    const existing = groups.get(key);
    if (existing) {
      existing.files.push(file);
      if (existing.sortTime !== null) {
        existing.sortTime = Math.max(existing.sortTime, fileTime);
      }
      existing.earliestTime = Math.min(existing.earliestTime, fileTime);
      return;
    }
    groups.set(key, {
      key,
      kind,
      files: [file],
      sortTime: kind === "DEPOT_INITIAL" ? null : fileTime,
      earliestTime: fileTime,
      titleTime: kind === "REMEDIATION" ? remediationTime : null,
    });
  });

  return Array.from(groups.values())
    .sort((a, b) => {
      if (a.sortTime === null) return 1;
      if (b.sortTime === null) return -1;
      if (a.sortTime !== b.sortTime) return b.sortTime - a.sortTime;
      return a.key.localeCompare(b.key);
    })
    .map((group) => ({
      key: group.key,
      kind: group.kind,
      files: [...group.files].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
      title:
        group.kind === "DEPOT_INITIAL"
          ? `Dépôt initial (${group.files.length})`
          : `${group.kind === "REMEDIATION" ? "Remédiation" : "Dépôt"} du ${formatDate(
              new Date(group.titleTime ?? group.earliestTime)
            )} (${group.files.length})`,
    }));
}
